import type { HTMLAttributesWithRef } from 'types';
import { cn } from 'utils/classNames';
import { findNextContentFocusTarget, goToElement } from 'utils/dom';

interface Props extends HTMLAttributesWithRef<HTMLDivElement> {
	/** Show a small indicator slightly below the element on focus. */
	showIndicator?: boolean;
}

/**
 * Target for programmatic focus
 *
 * It tries to find the closest following proper content to move focus to, to
 * give users of assistive tools something meaningful. If nothing is found, it
 * keeps focus but doesn't show a standard outline since that can look a bit
 * broken when it surrounds something empty.
 */
export default function FocusTarget({
	className,
	ref,
	showIndicator = true,
	tabIndex = -1,
	...attrs
}: Props) {
	return (
		<div
			ref={ref}
			className={cn(
				'focus-outline-none outline-0',
				showIndicator && [
					'relative',
					'after:absolute',
					'after:top-1',
					'after:left-1/2',
					'after:-translate-x-1/2',
					'after:w-40',
					'after:max-w-full',
					'after:h-1.5',
					'after:rounded-full',
					'focus-visible:after:bg-current',
					'forced-colors:focus-visible:after:bg-[CanvasText]',
				],
				className,
			)}
			tabIndex={tabIndex}
			onFocus={(e) => {
				const target = findNextContentFocusTarget(e.currentTarget);
				if (target) {
					goToElement(target);
				}
			}}
			{...attrs}
		/>
	);
}
FocusTarget.displayName = 'FocusTarget';
